import { Check, ChevronDown, CircleX, Copy } from 'lucide-react';
import {
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Button } from './Button';
import { cx } from './cx';

/** A label, one control, and a hint or an error under it. */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  optional,
  className,
  children,
}: {
  label?: ReactNode;
  /** The control's id. */
  htmlFor?: string;
  hint?: ReactNode;
  /** Shown instead of the hint. */
  error?: ReactNode;
  optional?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cx('mz-field', className)}>
      {label != null && (
        <label className="mz-label" htmlFor={htmlFor}>
          {label}
          {optional && <span className="mz-label-optional"> (optional)</span>}
        </label>
      )}
      {children}
      {error ? (
        <div className="mz-error" role="alert">
          <CircleX size={14} strokeWidth={1.75} aria-hidden="true" className="mz-icon" />
          {error}
        </div>
      ) : (
        hint != null && <div className="mz-hint">{hint}</div>
      )}
    </div>
  );
}

export function Input({
  mono,
  invalid,
  className,
  ...input
}: { mono?: boolean; invalid?: boolean } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={cx('mz-input', mono && 'mz-input-mono', className)}
      aria-invalid={invalid || undefined}
      {...input}
    />
  );
}

export function Textarea({
  mono,
  invalid,
  className,
  ...textarea
}: { mono?: boolean; invalid?: boolean } & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={cx('mz-textarea', mono && 'mz-input-mono', className)}
      aria-invalid={invalid || undefined}
      {...textarea}
    />
  );
}

export function Select({
  options,
  className,
  ...select
}: {
  options: readonly { value: string; label: string }[];
} & SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className={cx('mz-select-wrap', className)}>
      <select className="mz-select" {...select}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown size={14} strokeWidth={1.75} aria-hidden="true" className="mz-icon" />
    </div>
  );
}

/** One choice that is saved with the rest of a form. */
export function Checkbox({
  label,
  hint,
  className,
  ...input
}: { label: ReactNode; hint?: ReactNode } & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  return (
    <label className={cx('mz-check', className)}>
      <input type="checkbox" {...input} />
      <span className="mz-check-text">
        {label}
        {hint != null && <span className="mz-hint">{hint}</span>}
      </span>
    </label>
  );
}

/** A setting that takes effect as soon as it changes. */
export function Switch({
  label,
  className,
  ...input
}: { label: ReactNode } & Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'role'>) {
  return (
    <label className={cx('mz-switch', className)}>
      {/* biome-ignore lint/a11y/useAriaPropsForRole: a native checkbox already reports its checked state */}
      <input type="checkbox" role="switch" {...input} />
      {label}
    </label>
  );
}

/** A command, id or link to copy. */
export function CopyField({
  value,
  prompt,
  copyLabel = 'Copy',
  className,
}: {
  value: string;
  /** “$” for terminal commands. */
  prompt?: string;
  copyLabel?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      // Some browsers refuse; the value stays selectable.
    }
  }

  return (
    <div className={cx('mz-copy', className)}>
      {prompt && (
        <span className="mz-copy-prompt" aria-hidden="true">
          {prompt}
        </span>
      )}
      <span className="mz-copy-value">{value}</span>
      <Button
        variant="ghost"
        size="sm"
        icon={copied ? Check : Copy}
        label={copied ? 'Copied' : `${copyLabel}: ${value}`}
        onClick={copy}
      />
    </div>
  );
}
